<script setup lang="ts">
import { onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
  defineProps<{
    open: boolean
    title: string
    saving?: boolean
    // Wider panel (max-w-2xl) for forms that contain a table.
    wide?: boolean
  }>(),
  {
    saving: false,
    wide: false,
  },
)

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()

function handleClose(): void {
  // Closing (backdrop click / Escape / close button) is blocked while a
  // submit is in flight — see frontend-ui-patterns.md.
  if (props.saving) return
  emit('close')
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && !props.saving) {
    emit('close')
  }
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown)
    } else {
      document.removeEventListener('keydown', handleKeyDown)
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="fixed inset-0 z-50 flex justify-end">
      <!-- Dimmed backdrop: click to close, blocked while saving -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" @click="handleClose" />

      <!-- Slide-over panel -->
      <div
        class="relative z-10 flex flex-col bg-slate-800 border-l border-slate-700 w-full h-full shadow-2xl"
        :class="wide ? 'max-w-2xl' : 'max-w-xl'"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
      >
        <!-- Sticky header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-slate-700 shrink-0">
          <h2 class="text-lg font-bold text-white">{{ title }}</h2>
          <button
            type="button"
            class="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
            :aria-label="t('admin.common.close')"
            @click="handleClose"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Scrollable body -->
        <div class="flex-1 overflow-y-auto px-6 py-6 space-y-4">
          <slot />
        </div>

        <!-- Sticky footer -->
        <div class="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-700">
          <slot name="footer" />
        </div>
      </div>
    </div>
  </Teleport>
</template>
